import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, auth, loadErrorKey, useSessions } from '../api';
import { ConfirmButton } from '../components/Form';
import { Badge, PageHeader } from '../components/Parts';
import { dateTime } from '../format';

/** Account › Active sessions (pilot slice 4): every device where this person is signed in. */
export function Sessions() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const sessions = useSessions();
  const [note, setNote] = useState('');

  const end = useMutation({
    mutationFn: (id: string) => auth.endSession(id),
    onSuccess: async (r) => {
      if (r.current) {
        qc.clear();
        navigate('/sign-in', { replace: true });
        return;
      }
      await qc.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
  const endOthers = useMutation({
    mutationFn: () => auth.endOtherSessions(),
    onSuccess: async (r) => {
      setNote(t('sessions.ended', { n: r.ended }));
      await qc.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
  const failure = end.error ?? endOthers.error;
  const others = (sessions.data?.length ?? 0) > 1;

  return (
    <>
      <PageHeader
        icon="phone"
        title={t('sessions.title')}
        desc={t('sessions.desc')}
        actions={others ? [{ label: t('sessions.endOthers'), icon: 'logout', onClick: () => endOthers.mutate() }] : []}
      />
      {sessions.isError ? <p className="form-error">{t(loadErrorKey(sessions.error))}</p> : null}
      {failure ? (
        <p className="form-error" role="alert">
          {failure instanceof ApiError ? failure.message : t('table.error')}
        </p>
      ) : null}
      {note ? (
        <p className="note" role="status">
          {note}
        </p>
      ) : null}
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('sessions.device')}</th>
                <th>{t('sessions.mahallu')}</th>
                <th>{t('sessions.role')}</th>
                <th>{t('sessions.started')}</th>
                <th>{t('sessions.lastSeen')}</th>
                <th>{t('col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sessions.isLoading ? (
                <tr>
                  <td colSpan={6} className="tbl-msg">
                    {t('sessions.loading')}
                  </td>
                </tr>
              ) : null}
              {sessions.data?.map((s) => (
                <tr key={s.id}>
                  <td>
                    <b>{s.device}</b>
                    {s.current ? (
                      <>
                        {' '}
                        <Badge tone="teal">{t('sessions.thisDevice')}</Badge>
                      </>
                    ) : null}
                  </td>
                  <td>{s.mahallu ?? '—'}</td>
                  <td>{s.role ? t(`role.${s.role}`) : '—'}</td>
                  <td className="num">{dateTime(s.createdAt, i18n.language)}</td>
                  <td className="num">{dateTime(s.lastSeenAt, i18n.language)}</td>
                  <td>
                    <ConfirmButton label={s.current ? t('header.signOut') : t('sessions.end')} disabled={end.isPending} onConfirm={() => end.mutate(s.id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
